import './PricingPlaybook.css'

const PricingPlaybook = () => {
  return (
    <div className="pricing-playbook-page">
      <div className="page-header">
        <div>
          <h2>AISec Enterprise Pricing & Negotiation Playbook</h2>
          <p className="subtitle">Price integrity, credential value, and long-term profitability</p>
        </div>
      </div>

      <div className="playbook-container">
        {/* Section 1: Enterprise Pricing & Negotiation Policy */}
        <section className="playbook-section">
          <h3>1. Enterprise Pricing & Negotiation Policy (One Page)</h3>
          
          <div className="policy-block">
            <h4>Purpose</h4>
            <p>
              This policy ensures AISec maintains <strong>price integrity, credential value, and long-term profitability</strong> while enabling reasonable enterprise negotiation flexibility.
            </p>
          </div>

          <div className="policy-block">
            <h4>List Prices (Non-Negotiable)</h4>
            <p>The following are <strong>list prices and floors</strong>:</p>
            <ul className="price-list">
              <li><strong>Enterprise Core:</strong> $1,999 / year (10–20 users)</li>
              <li><strong>Enterprise Professional:</strong> $4,999 / year (21–50 users)</li>
              <li><strong>Enterprise Advanced:</strong> From $9,999 / year (50+ users)</li>
              <li><strong>Enterprise Certification Exams:</strong> $149 per exam</li>
              <li><strong>Seat Expansion:</strong>
                <ul>
                  <li>$89 / user / year (courses + labs)</li>
                  <li>$149 / user / year (with certification)</li>
                </ul>
              </li>
            </ul>
            <p className="warning-note">⚠️ Direct price reductions to these figures are not permitted.</p>
          </div>

          <div className="policy-block">
            <h4>What May Be Negotiated (Approved Levers)</h4>
            <div className="levers-list">
              <div className="lever-item">
                <strong>1. Seat Buffers (Preferred Lever)</strong>
                <ul>
                  <li>Core: +5 temporary buffer seats</li>
                  <li>Professional: +10 temporary buffer seats</li>
                  <li>Advanced: Custom</li>
                </ul>
              </div>
              <div className="lever-item">
                <strong>2. Certification Flexibility</strong>
                <ul>
                  <li>Free allocation across roles</li>
                  <li>Rollover of unused exams (up to 12 months)</li>
                  <li>Retake vouchers (not free exams)</li>
                </ul>
              </div>
              <div className="lever-item">
                <strong>3. Commitment-Based Value</strong>
                <ul>
                  <li>2-year commitment: price lock + added value</li>
                  <li>3-year commitment: price lock + enhanced certification access</li>
                </ul>
              </div>
              <div className="lever-item">
                <strong>4. Commercial Timing</strong>
                <ul>
                  <li>Early renewal incentives</li>
                  <li>Quarter-end value adds</li>
                  <li>Flexible invoicing schedules</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="policy-block">
            <h4>What Is Not Allowed</h4>
            <ul className="not-allowed-list">
              <li>Discounting certification exam prices</li>
              <li>Reducing published tier prices</li>
              <li>Mixing features across tiers</li>
              <li>Offering free certifications in Core</li>
            </ul>
          </div>

          <div className="policy-block">
            <h4>Approval Thresholds</h4>
            <div className="approval-table-container">
              <table className="approval-table">
                <thead>
                  <tr>
                    <th>Concession Type</th>
                    <th className="center-header">Sales</th>
                    <th className="center-header">Leadership</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Seat buffer ≤ 10</td>
                    <td className="check-cell">✔</td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td>Cert rollover</td>
                    <td className="check-cell">✔</td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td>Multi-year value add</td>
                    <td className="check-cell">✔</td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td>Custom Advanced deal</td>
                    <td>—</td>
                    <td className="check-cell">✔</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section 2: Sales Objection Responses */}
        <section className="playbook-section">
          <h3>2. Sales Objection Responses (Field-Ready)</h3>
          
          <div className="objection-block">
            <h4>Objection 1: "Your price is higher than competitors."</h4>
            <div className="response-box">
              <p><strong>Response:</strong></p>
              <p>
                We're not priced as a content platform. AISec is designed to build verifiable AI security capability, including certifications, labs, and audit-ready reporting. Most alternatives charge separately for those components.
              </p>
            </div>
          </div>

          <div className="objection-block">
            <h4>Objection 2: "We need a discount to proceed."</h4>
            <div className="response-box">
              <p><strong>Response:</strong></p>
              <p>
                We don't discount core access or certifications because that reduces long-term program value. What we <em>can</em> do is adjust how value is delivered, for example through seat buffers, certification flexibility, or multi-year commitments.
              </p>
            </div>
          </div>

          <div className="objection-block">
            <h4>Objection 3: "We won't use all the certifications."</h4>
            <div className="response-box">
              <p><strong>Response:</strong></p>
              <p>
                That's common. Certifications are pooled, role-agnostic, and can roll forward. Many customers use them later for promotions, audits, or role changes.
              </p>
            </div>
          </div>

          <div className="objection-block">
            <h4>Objection 4: "We only want courses, not certs."</h4>
            <div className="response-box">
              <p><strong>Response:</strong></p>
              <p>
                That's exactly why Enterprise Core exists. Professional is for organizations that need formal assurance and internal validation.
              </p>
            </div>
          </div>

          <div className="objection-block">
            <h4>Objection 5: "We're just piloting."</h4>
            <div className="response-box">
              <p><strong>Response:</strong></p>
              <p>
                Understood. Most pilots start with Core and upgrade once teams see adoption. We can also include upgrade credits to reduce friction.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Enterprise Procurement Negotiation Simulation */}
        <section className="playbook-section">
          <h3>3. Enterprise Procurement Negotiation Simulation</h3>
          
          <div className="simulation-block">
            <div className="scenario-header">
              <h4>Scenario</h4>
              <ul>
                <li><strong>Customer:</strong> Regulated financial services company</li>
                <li><strong>Size:</strong> 40 users</li>
                <li><strong>Interest:</strong> Enterprise Professional</li>
                <li><strong>Pressure:</strong> Procurement wants a 20% discount</li>
              </ul>
            </div>

            <div className="dialogue-block">
              <div className="dialogue-item procurement">
                <div className="dialogue-label">Procurement</div>
                <div className="dialogue-content">
                  We like the platform, but $4,999 is above budget. Can you do $4,000?
                </div>
              </div>

              <div className="dialogue-item aisec">
                <div className="dialogue-label">AISec (Correct Response)</div>
                <div className="dialogue-content">
                  We don't reduce list pricing because certifications and audit alignment need to retain their value. What we <em>can</em> do is make the deal work structurally.
                </div>
              </div>

              <div className="counter-offer">
                <h5>AISec Counter-Offer</h5>
                <ul>
                  <li>Maintain <strong>$4,999 list price</strong></li>
                  <li>Add <strong>+10 buffer seats</strong> (60 total)</li>
                  <li>Allow <strong>certification rollover for 12 months</strong></li>
                  <li>Lock pricing for <strong>Year 2</strong></li>
                </ul>
                <p className="counter-note">
                  This gives you additional capacity and flexibility without reducing program scope.
                </p>
              </div>

              <div className="dialogue-item procurement">
                <div className="dialogue-label">Procurement</div>
                <div className="dialogue-content">
                  That helps, but finance still wants something concrete.
                </div>
              </div>

              <div className="dialogue-item aisec">
                <div className="dialogue-label">AISec (Close)</div>
                <div className="dialogue-content">
                  If we proceed this quarter, we can also include <strong>5 additional certification attempts</strong> for future use. That's typically how our enterprise customers finalize approval.
                </div>
              </div>
            </div>

            <div className="outcome-box">
              <h5>Outcome</h5>
              <ul>
                <li>Customer feels negotiated value</li>
                <li>AISec preserves pricing integrity</li>
                <li>Renewal risk reduced</li>
                <li>Upsell path intact</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 4: Internal Reminder */}
        <section className="playbook-section reminder-section">
          <div className="reminder-box">
            <h3>Internal Reminder</h3>
            <p className="reminder-quote">
              <strong>Never sell cheaper. Always sell smarter.</strong>
            </p>
            <p>This playbook protects:</p>
            <ul>
              <li>Credential credibility</li>
              <li>Enterprise trust</li>
              <li>Long-term revenue</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  )
}

export default PricingPlaybook

